import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, InputGroup, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';

const PermintaanStock = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [newRequest, setNewRequest] = useState({
    inventory: '',
    quantity: 1,
    notes: '',
    requested_by: ''
  });
  const [username, setUsername] = useState('');

  useEffect(() => {
    fetchRequests();
    fetchInventoryItems();
    inspectBackendSchema();
    inspectInventoryRequestModel();
    
    // Ambil username dari localStorage atau JWT claim
    const storedUsername = localStorage.getItem('username');
    const storedUserFullName = localStorage.getItem('userFullName') || '';
    setUsername(storedUsername || 'unknown');
    
    // Debug nilai catatan saat komponen dimuat
    console.log('Initial notes value:', newRequest.notes);
  }, []);

  useEffect(() => {
    console.log('Current notes value:', newRequest.notes);
  }, [newRequest.notes]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }

      const response = await axios.get('https://rumahakrilik.id/api/inventory-requests/pending/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log data untuk debugging
      console.log('Fetched requests data:', response.data);

      // Pastikan field notes diproses dengan benar
      const processedRequests = (response.data || []).map(req => {
        // Pastikan req.notes tidak undefined, jika undefined gunakan string kosong
        if (req.notes === undefined) {
          req.notes = '';
        }
        return req;
      });

      setRequests(processedRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Gagal memuat data permintaan stock');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      
      const response = await axios.get('https://rumahakrilik.id/api/inventory/', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const items = Array.isArray(response.data) ? response.data : 
                    (response.data.results || []);

      setInventoryItems(items);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const inspectBackendSchema = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      // Coba dapatkan satu item untuk melihat struktur data yang diharapkan
      const response = await axios.options('https://rumahakrilik.id/api/inventory-requests/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Backend schema for inventory requests:', response.data);
      
      // Cari nama field yang digunakan untuk catatan
      if (response.data.actions && response.data.actions.POST) {
        console.log('Required fields:', response.data.actions.POST);
        console.log('Notes field information:', 
          response.data.actions.POST.notes || 
          response.data.actions.POST.note || 
          response.data.actions.POST.remarks || 
          response.data.actions.POST.description || 
          'Notes field not found'
        );
      }
    } catch (error) {
      console.error('Error getting schema:', error);
    }
  };

  const inspectInventoryRequestModel = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      console.log('Checking inventory request fields...');
      
      const getResponse = await axios.get('https://rumahakrilik.id/api/inventory-requests/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (getResponse.data && getResponse.data.length > 0) {
        console.log('Available fields in response:', Object.keys(getResponse.data[0]));
        console.log('Sample data:', getResponse.data[0]);
      }
      
      const optionsResponse = await axios.options('https://rumahakrilik.id/api/inventory-requests/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (optionsResponse.data && optionsResponse.data.actions) {
        console.log('Available fields from OPTIONS:', optionsResponse.data.actions.POST);
      }
    } catch (error) {
      console.error('Error inspecting inventory request model:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('jwtToken');
      
      const selectedItem = inventoryItems.find(item => item.id.toString() === newRequest.inventory.toString());
      
      if (!selectedItem) {
        toast.error('Pilih barang terlebih dahulu');
        return;
      }
      
      // Pastikan penggunaan field notes yang benar
      const requestData = {
        inventory: newRequest.inventory,
        item_name: selectedItem.name,
        quantity: parseInt(newRequest.quantity),
        requested_by: username, // Otomatis menggunakan username user yang login
        status: 'pending',
        order_id: '',
        notes: newRequest.notes || '' // Gunakan field notes sesuai model backend
      };

      console.log('Sending request data:', requestData);
      
      const response = await axios.post('https://rumahakrilik.id/api/inventory-requests/', requestData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response after creating request:', response.data);
      toast.success('Permintaan berhasil dibuat');
      setShowModal(false);
      fetchRequests();
      
      setNewRequest({ inventory: '', quantity: 1, notes: '', requested_by: '' });
    } catch (error) {
      console.error('Error creating request:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        const errorMessage = error.response.data.detail || 
                          (typeof error.response.data === 'object' && 
                           Object.values(error.response.data).join(', ')) ||
                          'Gagal membuat permintaan';
        toast.error(errorMessage);
      } else {
        toast.error('Gagal membuat permintaan');
      }
    }
  };

  // Perbaiki fungsi handleApprove
  const handleApprove = async (requestId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const username = localStorage.getItem('username') || 'unknown';
      
      console.log(`Attempting to approve request ${requestId}`);
      
      // Gunakan metode PATCH untuk mengubah status seperti di handleReject
      const response = await axios.patch(`https://rumahakrilik.id/api/inventory-requests/${requestId}/`, {
        status: 'approved',
        approved_by: username,
        approved_date: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Approve response:', response.data);
      toast.success('Permintaan disetujui');
      fetchRequests(); // Refresh data
    } catch (error) {
      console.error('Error approving request:', error);
      
      // Tampilkan detail error lebih spesifik
      if (error.response) {
        console.error('Response data:', error.response.data);
        const errorMessage = error.response.data.detail || 
                          (typeof error.response.data === 'object' && 
                           Object.values(error.response.data).join(', ')) ||
                          'Gagal menyetujui permintaan';
        toast.error(errorMessage);
      } else {
        toast.error('Gagal menyetujui permintaan');
      }
    }
  };

  // Tambahkan logging di handleReject untuk debugging
  const handleReject = async (requestId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      console.log(`Attempting to reject request ${requestId}`);
      
      const response = await axios.patch(`https://rumahakrilik.id/api/inventory-requests/${requestId}/`, {
        status: 'rejected'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Reject response:', response.data);
      toast.success('Permintaan ditolak');
      fetchRequests(); // Refresh data
    } catch (error) {
      console.error('Error rejecting request:', error);
      if (error.response) {
        console.error('Reject error response:', error.response.data);
      }
      toast.error('Gagal menolak permintaan');
    }
  };

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">Permintaan Stock</h1>
        
        <div className="d-flex justify-content-end mb-3">
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle me-2"></i>
            Buat Permintaan Baru
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Memuat data permintaan...</p>
          </div>
        ) : (
          <>
            {requests.length === 0 ? (
              <div className="text-center py-5">
                <p>Tidak ada permintaan stock yang menunggu</p>
              </div>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nama Barang</th>
                    <th>Jumlah</th>
                    <th>Pemohon</th>
                    <th>Tanggal Permintaan</th>
                    <th>Status</th>
                    <th>Catatan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.id}</td>
                      <td>{request.item_name}</td>
                      <td>{request.quantity}</td>
                      <td>{request.requested_by}</td>
                      <td>{new Date(request.request_date).toLocaleDateString('id-ID')}</td>
                      <td>
                        <span className="badge bg-warning">Menunggu</span>
                      </td>
                      <td>{request.notes || '-'}</td>
                      <td>
                        <Button 
                          variant="success" 
                          size="sm" 
                          className="me-1" 
                          onClick={() => handleApprove(request.id)}
                        >
                          <i className="bi bi-check-circle"></i> Setujui
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleReject(request.id)}
                        >
                          <i className="bi bi-x-circle"></i> Tolak
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* Modal Permintaan Baru */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Buat Permintaan Stock Baru</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Pilih Barang</Form.Label>
                <Form.Select 
                  required
                  value={newRequest.inventory}
                  onChange={(e) => setNewRequest({...newRequest, inventory: e.target.value})}
                >
                  <option value="">-- Pilih Barang --</option>
                  {inventoryItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} - Stok: {item.current_stock} {item.unit}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Jumlah</Form.Label>
                <Form.Control 
                  type="number"
                  min="1"
                  required
                  value={newRequest.quantity}
                  onChange={(e) => setNewRequest({...newRequest, quantity: parseInt(e.target.value)})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Catatan</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3}
                  value={newRequest.notes}
                  onChange={(e) => {
                    console.log('Setting notes to:', e.target.value);
                    setNewRequest({...newRequest, notes: e.target.value})
                  }}
                  placeholder="Masukkan catatan permintaan disini..."
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                  Batal
                </Button>
                <Button variant="primary" type="submit">
                  Kirim Permintaan
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default PermintaanStock;